/**
 * Beat 10's fixed-step watcher: it knows the bell-for-gap trade, not the room
 * or its picture. No Three, Rapier, or room coordinates enter here; stage 4's
 * queue and stage 5's run are deliberately not premature states in this dog.
 */
import { ROSALIE_TUNING } from '@/physics/character';
import type { Position, WatcherSuspicion } from '@/systems/stealth';

/**
 * Rosalie's committed 1.9 m/s trot: the bell trade stays legible against the
 * player's own movement; a faster or slower copied value would change it alone.
 */
export const INVESTIGATE_SPEED = ROSALIE_TUNING.trotSpeed;
/**
 * 3.2 m/s lies between her trot and roll caps, so sight forces a roll without
 * making escape impossible; below trot she walks away, at roll it is a trap.
 */
export const DRIVE_SPEED = 3.2;
/** 3 rad/s makes a bell bearing visible in its body; a higher rate snaps, a lower one cannot track it. */
export const TURN_RATE = 3.0;
/** 1 m stops it at a sound rather than on Holly; a smaller radius crowds her, a larger one weakens the gap. */
export const ARRIVE_RADIUS = 1.0;
/** 6 s is the authored, unplayed opening; longer trivializes the trade and shorter removes its crossing time. */
export const CAST_SECONDS = 6.0;
/** 0.45 rad gives the body a readable search arc; less disappears, more wrongly sweeps the gap. */
export const CAST_SWEEP = 0.45;
/** 0.18 Hz makes one out-and-back almost fill 6 s; faster looks agitated, slower fails to search. */
export const CAST_HERTZ = 0.18;
/** 20 m leaves every obstacle outside reachable ground; more needs avoidance, less prevents the intended bell pull. */
export const LEASH_RADIUS = 20.0;
/** 0.6 m is the dog's body edge; a smaller inset clips field limits, a larger one shortens its authored ground. */
export const DOG_BODY_MARGIN = 0.6;
/** 4 s lets it commit to the last sighted place; less abandons sight too soon, more erases cover's escape. */
export const DRIVE_GIVE_UP_SECONDS = 4.0;
/** 1.4 m is a horizontal lunge; larger catches at a distance, smaller lets bodies visibly overlap. */
export const CATCH_RADIUS = 1.4;
/** 2 s reaches black and 1.2 s returns: changing either breaks the authored consequence's readable rhythm. */
export const TURN_BACK = { blackAt: 2.0, backAt: 1.2 } as const;

export type NightDogPhase = 'post' | 'investigating' | 'casting' | 'returning' | 'driving';

export interface NightDogOptions {
  readonly post: { readonly x: number; readonly z: number; readonly facing: number };
  readonly bankZ: number;
  /** The field fence's south face; the dog must not cross into the apron. */
  readonly fenceZ: number;
  readonly bounds: { readonly westX: number; readonly eastX: number; readonly northZ: number };
}

export interface NightDogEvent {
  readonly kind: 'cast' | 'drive';
  readonly position: Position;
}

export interface NightDogSnapshot {
  readonly x: number;
  readonly z: number;
  readonly facing: number;
  readonly phase: NightDogPhase;
  readonly targetX: number;
  readonly targetZ: number;
  readonly distanceFromPost: number;
  readonly investigations: number;
  /**
   * How many times it has launched a drive. Monotonic, and it is an instrument.
   *
   * **A drive is a 1.9 s transient and a poll with a 5 s slice steps clean over
   * it**, so a gate that samples `phase === 'driving'` is a race that happens to
   * win. This counter is the latched fact behind it; see the note in
   * `beat-10-dog.spec.ts` about the gate that was green for the wrong reason.
   */
  readonly drives: number;
  readonly turnBack: { readonly phase: 'out' | 'in' | null; readonly elapsed: number };
  readonly placeRosalie: boolean;
}

function turnToward(current: number, target: number, maximum: number): number {
  const delta = Math.atan2(Math.sin(target - current), Math.cos(target - current));
  return current + Math.max(-maximum, Math.min(maximum, delta));
}

export class NightDog {
  readonly #options: NightDogOptions;
  #x: number;
  #z: number;
  #facing: number;
  #phase: NightDogPhase = 'post';
  #targetX: number;
  #targetZ: number;
  #castHeading = 0;
  #castElapsed = 0;
  #lostElapsed = 0;
  #heardCount: number | null = 0;
  #investigations = 0;
  #drives = 0;
  #turnBack: 'out' | 'in' | null = null;
  #turnElapsed = 0;
  #placeRosalie = false;

  constructor(options: NightDogOptions) {
    if (!(DRIVE_SPEED > ROSALIE_TUNING.trotSpeed && DRIVE_SPEED < ROSALIE_TUNING.rollMaxSpeed)) {
      throw new Error('night dog drive speed must remain between trot and roll');
    }
    this.#options = options;
    this.#x = options.post.x;
    this.#z = options.post.z;
    this.#facing = options.post.facing;
    this.#targetX = this.#x;
    this.#targetZ = this.#z;
  }

  /** Clamp in room order: bank, field fence, inset field, then the post-centred leash. */
  #setTarget(x: number, z: number): void {
    const bankedZ = Math.max(this.#options.bankZ, z);
    // The southern bank only prevents a dog entering the ditch. The field fence
    // is its symmetric northern limit: without it, a sound on the apron pulls
    // this body through the boards the player still has to use as a doorway.
    const fieldZ = Math.min(this.#options.fenceZ - DOG_BODY_MARGIN, bankedZ);
    const insetX = Math.max(this.#options.bounds.westX + DOG_BODY_MARGIN, Math.min(this.#options.bounds.eastX - DOG_BODY_MARGIN, x));
    const insetZ = Math.min(this.#options.bounds.northZ - DOG_BODY_MARGIN, fieldZ);
    const dx = insetX - this.#options.post.x;
    const dz = insetZ - this.#options.post.z;
    const distance = Math.hypot(dx, dz);
    if (distance <= LEASH_RADIUS) {
      this.#targetX = insetX;
      this.#targetZ = insetZ;
      return;
    }
    this.#targetX = this.#options.post.x + dx / distance * LEASH_RADIUS;
    this.#targetZ = this.#options.post.z + dz / distance * LEASH_RADIUS;
  }

  #enterCasting(y: number, events: NightDogEvent[]): void {
    this.#phase = 'casting';
    this.#castElapsed = 0;
    this.#castHeading = this.#facing;
    this.#investigations += 1;
    events.push({ kind: 'cast', position: { x: this.#x, y, z: this.#z } });
  }

  #enterDriving(y: number, events: NightDogEvent[]): void {
    this.#phase = 'driving';
    this.#drives += 1;
    events.push({ kind: 'drive', position: { x: this.#x, y, z: this.#z } });
  }

  #moveTowardTarget(dt: number, speed: number): boolean {
    const dx = this.#targetX - this.#x;
    const dz = this.#targetZ - this.#z;
    const distance = Math.hypot(dx, dz);
    if (distance <= ARRIVE_RADIUS) return true;
    const bearing = Math.atan2(dx, dz);
    this.#facing = turnToward(this.#facing, bearing, TURN_RATE * dt);
    const travelled = Math.min(speed * dt, distance);
    this.#x += dx / distance * travelled;
    this.#z += dz / distance * travelled;
    return distance - travelled <= ARRIVE_RADIUS;
  }

  #stepTurnBack(dt: number): boolean {
    if (this.#turnBack === null) return false;
    this.#turnElapsed += dt;
    if (this.#turnBack === 'out' && this.#turnElapsed >= TURN_BACK.blackAt) {
      this.#placeRosalie = true;
      this.#turnBack = 'in';
      this.#turnElapsed = 0;
      return true;
    }
    if (this.#turnBack === 'in' && this.#turnElapsed >= TURN_BACK.backAt) {
      this.#turnBack = null;
      this.#turnElapsed = 0;
    }
    return true;
  }

  #hearMostRecentSound(watcher: WatcherSuspicion): void {
    if (this.#heardCount === null) {
      this.#heardCount = watcher.soundsHeard;
      return;
    }
    if (this.#phase !== 'driving' && watcher.soundsHeard > this.#heardCount && watcher.lastSound !== null) {
      this.#heardCount = watcher.soundsHeard;
      this.#setTarget(watcher.lastSound.position.x, watcher.lastSound.position.z);
      this.#phase = 'investigating';
      return;
    }
    this.#heardCount = Math.max(this.#heardCount, watcher.soundsHeard);
  }

  #stepInvestigation(dt: number, y: number, events: NightDogEvent[]): void {
    if (this.#moveTowardTarget(dt, INVESTIGATE_SPEED)) this.#enterCasting(y, events);
  }

  #stepCasting(dt: number): void {
    this.#castElapsed += dt;
    this.#facing = this.#castHeading + Math.sin(this.#castElapsed * Math.PI * 2 * CAST_HERTZ) * CAST_SWEEP;
    if (this.#castElapsed < CAST_SECONDS) return;
    this.#setTarget(this.#options.post.x, this.#options.post.z);
    this.#phase = 'returning';
  }

  #stepDrive(dt: number, rosalie: Position, watcher: WatcherSuspicion, y: number, events: NightDogEvent[]): void {
    // ── "While it can see her" is VISIBILITY, not `state === 'detected'` ─────
    //
    // Suspicion is a meter with a two-second grace and a slow decay, so it sits
    // at 100 — and reports `'detected'` — for seconds after she has gone. A
    // drive that re-targets on that keeps steering at a pig it cannot see, and
    // **that deletes the counter-play**: breaking the sight line, dropping into
    // the channel, being somewhere else are the whole of what this beat gives
    // her back, and each of them would have bought two seconds of nothing.
    //
    // `lastVisibility.visibility` is the product of cone, range, exposure and
    // the ray, so it is zero the instant any one of them is — which is exactly
    // when a dog stops being able to see. The target then freezes where she
    // last was and `DRIVE_GIVE_UP_SECONDS` runs from that moment.
    //
    // Measured: with the suspicion test, a respawn 23 m away left the target
    // tracking her for a further 1.9 s and it followed her to the far spawn.
    if (watcher.lastVisibility.visibility > 0) {
      this.#setTarget(rosalie.x, rosalie.z);
      this.#lostElapsed = 0;
    } else {
      this.#lostElapsed += dt;
    }
    if (Math.hypot(this.#x - rosalie.x, this.#z - rosalie.z) <= CATCH_RADIUS) {
      this.#turnBack = 'out';
      this.#turnElapsed = 0;
      this.#setTarget(this.#options.post.x, this.#options.post.z);
      this.#phase = 'returning';
      return;
    }
    if (this.#lostElapsed >= DRIVE_GIVE_UP_SECONDS) {
      this.#enterCasting(y, events);
      return;
    }
    this.#moveTowardTarget(dt, DRIVE_SPEED);
  }

  step(dt: number, rosalie: Position, watcher: WatcherSuspicion | undefined): readonly NightDogEvent[] {
    if (watcher === undefined) throw new Error('night dog requires its watcher snapshot');
    if (!Number.isFinite(dt) || dt < 0) throw new Error('night dog step must use a finite non-negative dt');
    const events: NightDogEvent[] = [];
    this.#placeRosalie = false;
    this.#hearMostRecentSound(watcher);
    // ── The drive latch is refused while a turn-back is in flight ────────────
    //
    // **`this.#turnBack === null` is load-bearing and was missing.** The catch
    // sets `turnBack = 'out'` and leaves the phase `'returning'`, while her
    // suspicion is not cleared until the black frame two seconds later — so on
    // the very next tick `state` was still `'detected'`, the phase was not yet
    // `'driving'`, and this line latched a second drive and pushed a `drive`
    // event that voiced itself over a screen going black. `CorridorDirector`
    // guards its capture the same way and says why: *"a second detection
    // landing on a player who cannot act is the unexplainable detection spec
    // §5.1 forbids"*. Beat 10 does not get to re-open a window beat 4 closed.
    if (this.#turnBack === null && watcher.state === 'detected' && this.#phase !== 'driving') {
      this.#enterDriving(rosalie.y, events);
    }
    if (this.#stepTurnBack(dt)) return events;
    if (this.#phase === 'investigating') {
      this.#stepInvestigation(dt, rosalie.y, events);
    } else if (this.#phase === 'casting') {
      this.#stepCasting(dt);
    } else if (this.#phase === 'returning') {
      if (this.#moveTowardTarget(dt, INVESTIGATE_SPEED)) this.#phase = 'post';
    } else if (this.#phase === 'driving') {
      this.#stepDrive(dt, rosalie, watcher, rosalie.y, events);
    } else {
      this.#facing = turnToward(this.#facing, this.#options.post.facing, TURN_RATE * dt);
    }
    return events;
  }

  /**
   * How far it has strayed from its post, without allocating.
   *
   * `snapshot()` carries the same number and is what every gate reads, but the
   * fixed step now needs it EVERY TICK — `main.ts` derives the herd's balk from
   * it (D-184) — and the beat's own plan forbids per-frame allocation in the
   * fixed step. One `hypot` against an object per tick is the difference.
   */
  distanceFromPost(): number {
    return Math.hypot(this.#x - this.#options.post.x, this.#z - this.#options.post.z);
  }

  snapshot(): NightDogSnapshot {
    return {
      x: this.#x,
      z: this.#z,
      facing: this.#facing,
      phase: this.#phase,
      targetX: this.#targetX,
      targetZ: this.#targetZ,
      distanceFromPost: this.distanceFromPost(),
      investigations: this.#investigations,
      drives: this.#drives,
      turnBack: { phase: this.#turnBack, elapsed: this.#turnElapsed },
      placeRosalie: this.#placeRosalie,
    };
  }

  reset(): void {
    this.#x = this.#options.post.x;
    this.#z = this.#options.post.z;
    this.#facing = this.#options.post.facing;
    this.#targetX = this.#x;
    this.#targetZ = this.#z;
    this.#phase = 'post';
    this.#castElapsed = 0;
    this.#lostElapsed = 0;
    this.#placeRosalie = false;
    // `turnBack.phase !== null` is the fixed-step re-entry guard, so this reset
    // must leave an active `out -> in -> null` timeline intact. It is called at
    // black to restore the field, but clearing `in` here made the overlay reopen
    // at once and admitted a second detection 1.2 s too early; presentation is
    // never read to decide that guard.
    if (this.#turnBack === null) this.#turnElapsed = 0;
    // Zero would replay an already-heard bell; adopt the next snapshot instead.
    this.#heardCount = null;
  }
}
